import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";

// ============================================================
// FYUNOR — Button System
// Variants: primary · secondary · ghost · text · icon
// Primary uses a "Pressed Deep" effect: outer glow on hover,
// 1px downward displacement on active.
// ============================================================

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium select-none transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary:
          "bg-primary text-primary-foreground shadow-sm hover:shadow-glow hover:bg-primary-hover active:translate-y-px",
        secondary:
          "bg-secondary text-secondary-foreground border border-border hover:bg-surface-3 active:translate-y-px",
        ghost:
          "text-foreground hover:bg-surface-2 active:bg-surface-3",
        text:
          "text-primary hover:text-primary-hover underline-offset-4 hover:underline rounded-md",
        destructive:
          "bg-error text-error-foreground shadow-sm hover:bg-error/90 active:translate-y-px",
        outline:
          "border border-border bg-transparent text-foreground hover:bg-surface-2 active:bg-surface-3",
      },
      size: {
        default: "h-10 px-4 py-2 min-h-[40px]",
        sm: "h-9 rounded-lg px-3 text-xs min-h-[36px]",
        lg: "h-12 rounded-xl px-8 text-base min-h-[48px]",
        icon: "h-10 w-10 min-h-[40px] min-w-[40px]",
        "icon-sm": "h-9 w-9 rounded-lg",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  }
);

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
  );
});
Button.displayName = "Button";

export { Button, buttonVariants };